import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Constants } from "../constants/appConstants";
import { AuthService } from "../services/Auth/AuthService";
import { AuthenticationFactory } from "../services/Auth/AuthenticationFactory";
import { AppUser } from "../Models/AppUser";
import { jwtDecode } from "jwt-decode";
import { useUserActivityTokenRefresh } from "../hooks/useUserActivityTokenRefresh";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { User } from "oidc-client-ts";
import httpService from "../services/httpService";
import { oidcUserManager } from "../auth/userManager";
import { readCrossTabActivity, writeCrossTabActivity, subscribeCrossTabActivity } from "../auth/crossTabActivity";
import { broadcastCrossTabLogout, subscribeCrossTabLogout, isCrossTabLogoutInProgress, waitForCrossTabLogoutAcknowledgement } from "../auth/crossTabSession";

export type AuthProviderType = "cognito" | "keycloak" | "siteminder";

interface AuthContextType {
  authService: AuthService;
  authProvider: AuthProviderType;
  authType: AuthProviderType;
  isAuthenticated: boolean;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  appUser: AppUser | null;
  setUser: React.Dispatch<React.SetStateAction<any>>;
  isUserValidated: boolean;
  setIsUserValidated: React.Dispatch<React.SetStateAction<boolean>>;
  signIn: (username?: string, password?: string) => Promise<void>;
  signOut: () => Promise<void>;
  isAdministrator: () => boolean;
  isSuperAdministrator: () => boolean;
  hasActiveSession: () => Promise<boolean>;
  isFinalReviewer: () => boolean;
  isSmeReviewer: () => boolean;
  isInitialReviewer: () => boolean;
  showInactivityWarning: boolean;
  extendSession: () => void;
  currentUserRoles: () => string[];
  isAuthorizedUser: (roles: string[]) => boolean;
}

const defaultAuthContext: AuthContextType = {
  authService: {} as AuthService,
  authProvider: "siteminder",
  authType: "siteminder",
  isAuthenticated: false,
  setIsAuthenticated: () => {},
  appUser: null,
  setUser: () => {},
  isUserValidated: false,
  setIsUserValidated: () => {},
  signIn: async () => {},
  signOut: async () => {},
  hasActiveSession: async () => false,
  isAdministrator: () => false,
  isSuperAdministrator: () => false,
  isFinalReviewer: () => false,
  isSmeReviewer: () => false,
  isInitialReviewer: () => false,
  showInactivityWarning: false,
  extendSession: () => {},
  currentUserRoles: () => [],
  isAuthorizedUser: (roles: string[]) => roles.length === 0,
};

export const AuthContext = createContext<AuthContextType>(defaultAuthContext);

interface AuthProviderProps {
  children: ReactNode;
  authProvider: AuthProviderType;
}

const getEnvNumber = (value: string | undefined, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const normalizeStringValue = (value: string | null | undefined): string => {
  if (!value) {
    return "";
  }

  return String(value).trim().replace(/^['"]+|['"]+$/g, "");
};

const IDLE_WARNING_AFTER_MINUTES = getEnvNumber(
  import.meta.env.VITE_IDLE_WARNING_AFTER_MINUTES,
  10
);
const IDLE_WARNING_COUNTDOWN_MINUTES = getEnvNumber(
  import.meta.env.VITE_IDLE_WARNING_COUNTDOWN_MINUTES,
  5
);

const IDLE_WARNING_AFTER_MS = IDLE_WARNING_AFTER_MINUTES * 60 * 1000;
const IDLE_WARNING_COUNTDOWN_SECONDS = IDLE_WARNING_COUNTDOWN_MINUTES * 60;

const TOKEN_WARNING_BEFORE_EXP_SECONDS = 75;
const TOKEN_EXPIRY_BUFFER_SECONDS = 15;
const TOKEN_WARNING_ACTIVE_GRACE_MS = 2 * 60 * 1000;
const TOKEN_WARNING_RECENT_ACTIVITY_RECHECK_MS = 10 * 1000;
const TOKEN_WATCHDOG_INTERVAL_MS = 5000;

const MIN_VALID_SECONDS_AFTER_EXTEND = 30;
const SAME_TOKEN_WARNING_SUPPRESS_MS = 3 * 60 * 1000;
const RENEW_BLOCK_UNTIL_KEY = "siteminderRenewBlockedUntil";
const RENEW_BLOCK_REASON_KEY = "siteminderRenewBlockReason";

const EXTEND_SAME_TOKEN_RETRY_DELAY_MS = 1500;
const EXTEND_SAME_TOKEN_MAX_RETRIES = 4;

const ACTIVITY_EVENTS: Array<keyof DocumentEventMap> = [
  "keydown",
  "click",
  "pointerdown",
  "mousedown",
  "touchstart",
  "input",
  "change",
  "focusin",
];

type WarningReason = "idle-threshold" | "token-expiring";

export const AuthProvider: React.FC<AuthProviderProps> = ({
  authProvider,
  children,
}) => {
  const normalizedAuthProvider = useMemo(() => {
    return normalizeStringValue(authProvider).toLowerCase() as AuthProviderType;
  }, [authProvider]);

  const authService = useMemo(() => {
    sessionStorage.setItem("authProvider", normalizedAuthProvider);
    return AuthenticationFactory.getAuthService(normalizedAuthProvider);
  }, [normalizedAuthProvider]);

  const silentUserManager = oidcUserManager;

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [_, setUser] = useState<any | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [isUserValidated, setIsUserValidated] = useState(false);

  const [accessTokenExp, setAccessTokenExp] = useState<number | null>(null);

  const [showInactivityWarning, setShowInactivityWarning] =
    useState<boolean>(false);
  const [countdownSeconds, setCountdownSeconds] = useState<number>(0);
  const [userChoseLogout, setUserChoseLogout] = useState<boolean>(false);
  const [isExtendingSession, setIsExtendingSession] = useState<boolean>(false);
  const [warningReason, setWarningReason] = useState<WarningReason | null>(null);

  const mounted = useRef(true);
  const showWarningRef = useRef<boolean>(false);
  const userChoseLogoutRef = useRef<boolean>(false);

  const lastInteractionAtRef = useRef<number>(Date.now());

  const idleWarningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const tokenWarningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const warningDeadlineMsRef = useRef<number | null>(null);
  const warningReasonRef = useRef<WarningReason | null>(null);

  const acceptedSameTokenExpRef = useRef<number | null>(null);
  const acceptedSameTokenSuppressUntilRef = useRef<number | null>(null);

  const clearRenewBlockState = useCallback(() => {
    try {
      sessionStorage.removeItem(RENEW_BLOCK_UNTIL_KEY);
      sessionStorage.removeItem(RENEW_BLOCK_REASON_KEY);
    } catch (err) {
      console.warn("[AuthContext] Failed clearing renew block state.", err);
    }
  }, []);

  const clearClientAuthState = useCallback(
    (reason: string) => {
      try {
        console.warn("[AuthContext] Clearing client auth state.", { reason });

        sessionStorage.removeItem("appUser");
        sessionStorage.removeItem("accessTokenExp");
        sessionStorage.removeItem("authenticated");
        sessionStorage.removeItem("access_token");
        sessionStorage.removeItem("id_token");
        sessionStorage.removeItem("authorizationToken");
        sessionStorage.removeItem("authorizationTokenExp");
        

        clearRenewBlockState();

        setAppUser(null);
        setUser(null);
        setAccessTokenExp(null);
        setIsAuthenticated(false);
        setIsUserValidated(false);
      } catch (err) {
        console.error("[AuthContext] Failed clearing client auth state.", err);
      }
    },
    [clearRenewBlockState]
  );

  const writeSharedLastActivityAt = useCallback((ts: number) => {
    try {
      writeCrossTabActivity(ts);
    } catch (err) {
      console.warn(
        "[AuthContext] Failed to write shared last activity timestamp.",
        err
      );
    }
  }, []);

  const readSharedLastActivityAt = useCallback((): number | null => {
    try {
      const raw = readCrossTabActivity();
      if (!raw) {
        return null;
      }

      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : null;
    } catch (err) {
      console.warn(
        "[AuthContext] Failed to read shared last activity timestamp.",
        err
      );
      return null;
    }
  }, []);

  const getEffectiveLastActivityAt = useCallback((): number => {
    const sharedLastActivityAt = readSharedLastActivityAt();

    if (
      sharedLastActivityAt &&
      sharedLastActivityAt > lastInteractionAtRef.current
    ) {
      lastInteractionAtRef.current = sharedLastActivityAt;
    }

    return lastInteractionAtRef.current;
  }, [readSharedLastActivityAt]);

  const isRecentlyActiveForTokenWarning = useCallback((): boolean => {
    const effectiveLastActivityAt = getEffectiveLastActivityAt();
    const idleMs = Date.now() - effectiveLastActivityAt;
    return idleMs <= TOKEN_WARNING_ACTIVE_GRACE_MS;
  }, [getEffectiveLastActivityAt]);

  const clearSameTokenWarningSuppression = useCallback(() => {
    acceptedSameTokenExpRef.current = null;
    acceptedSameTokenSuppressUntilRef.current = null;
  }, []);

  const getAccessTokenFromUser = useCallback((user: any): string | null => {
    return user?.access_token || user?.accessToken || null;
  }, []);

  const buildAppUserFromOidcUser = useCallback((user: User): AppUser => {
    return {
      name: user.profile?.name ?? "",
      email: user.profile?.email ?? "",
      phone: user.profile?.phone as string,
      groups: user.profile?.role
        ? Array.isArray(user.profile.role)
          ? user.profile.role
          : [user.profile.role]
        : [],
      idToken: user.id_token,
      accessToken: user.access_token,
    };
  }, []);

  const preserveRolesAfterRefresh = useCallback((refreshedUser: AppUser): AppUser => {
    const appRoleNames = [
      Constants.ADMIN_GROUP_NAME?.toLowerCase(),
      Constants.SUPER_ADMIN_GROUP_NAME?.toLowerCase(),
      Constants.FINAL_REVIEWER_GROUP_NAME?.toLowerCase(),
      Constants.SME_REVIEWER_GROUP_NAME?.toLowerCase(),
      Constants.INITIAL_REVIEWER_GROUP_NAME?.toLowerCase(),
    ];

    const refreshedGroups = refreshedUser?.groups || [];
    const refreshedHasAppRole = refreshedGroups.some((group: string) =>
      appRoleNames.includes(group?.toLowerCase())
    );

    if (refreshedHasAppRole) {
      return refreshedUser;
    }

    try {
      const storedUserRaw = sessionStorage.getItem("appUser");
      const storedUser = storedUserRaw ? (JSON.parse(storedUserRaw) as AppUser) : null;

      const existingGroups =
        appUser?.groups && appUser.groups.length > 0
          ? appUser.groups
          : storedUser?.groups || [];

      const existingHasAppRole = existingGroups.some((group: string) =>
        appRoleNames.includes(group?.toLowerCase())
      );

      if (!existingHasAppRole) {
        return refreshedUser;
      }

      return {
        ...storedUser,
        ...refreshedUser,
        groups: existingGroups,
      };
    } catch (error) {
      console.warn("[AuthContext] Failed to preserve roles after session refresh.", error);
      return refreshedUser;
    }
  }, [appUser]);

  const getBufferedExp = useCallback((exp: number | null): number | null => {
    if (!exp) {
      return null;
    }

    return Math.max(0, exp - TOKEN_EXPIRY_BUFFER_SECONDS);
  }, []);

  const getRealSecondsLeftFromExp = useCallback(
    (exp: number | null): number | null => {
      if (!exp) {
        return null;
      }

      const now = Math.floor(Date.now() / 1000);
      return exp - now;
    },
    []
  );

  const getSecondsLeftFromExp = useCallback(
    (exp: number | null): number | null => {
      const bufferedExp = getBufferedExp(exp);
      if (!bufferedExp) {
        return null;
      }

      const now = Math.floor(Date.now() / 1000);
      return bufferedExp - now;
    },
    [getBufferedExp]
  );

  const wait = useCallback((ms: number) => {
    return new Promise<void>((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }, []);

  const readStoredAccessTokenExp = useCallback((): number | null => {
    try {
      const storedAccessToken = sessionStorage.getItem("access_token");
      if (!storedAccessToken) {
        sessionStorage.removeItem("accessTokenExp");
        return null;
      }

      const decoded: { exp: number } = jwtDecode(storedAccessToken);
      if (decoded?.exp) {
        sessionStorage.setItem("accessTokenExp", String(decoded.exp));
        return decoded.exp;
      }
    } catch (err) {
      console.warn("[AuthContext] Failed reading stored accessTokenExp", err);
    }

    return null;
  }, []);

  const syncAccessTokenExpFromToken = useCallback((token: string | null) => {
    if (!token) {
      setAccessTokenExp(null);
      sessionStorage.removeItem("accessTokenExp");
      return null;
    }

    try {
      const decoded: { exp: number } = jwtDecode(token);
      setAccessTokenExp(decoded.exp);
      sessionStorage.setItem("accessTokenExp", String(decoded.exp));
      console.log(
        "[AuthContext] Token exp:",
        decoded.exp,
        new Date(decoded.exp * 1000).toLocaleString()
      );
      return decoded.exp;
    } catch (e) {
      console.warn("[AuthContext] Failed to decode access token:", e);
      return null;
    }
  }, []);

  const getEffectiveAccessTokenExp = useCallback((): number | null => {
    const liveExp = readStoredAccessTokenExp();

    if (liveExp !== accessTokenExp) {
      setAccessTokenExp(liveExp);
    }

    return liveExp;
  }, [accessTokenExp, readStoredAccessTokenExp]);

  const getEffectiveBufferedAccessTokenExp = useCallback((): number | null => {
    return getBufferedExp(getEffectiveAccessTokenExp());
  }, [getBufferedExp, getEffectiveAccessTokenExp]);

  const clearAllTimers = useCallback(() => {
    if (idleWarningTimeoutRef.current) {
      clearTimeout(idleWarningTimeoutRef.current);
      idleWarningTimeoutRef.current = null;
    }

    if (tokenWarningTimeoutRef.current) {
      clearTimeout(tokenWarningTimeoutRef.current);
      tokenWarningTimeoutRef.current = null;
    }
  }, []);

  const clearWarningState = useCallback(() => {
    showWarningRef.current = false;
    warningDeadlineMsRef.current = null;
    warningReasonRef.current = null;
    setShowInactivityWarning(false);
    setCountdownSeconds(0);
    setUserChoseLogout(false);
    setWarningReason(null);
  }, []);

  const cleanupStaleDialogArtifacts = useCallback(() => {
    try {
      const hasVisibleWarning = !!document.querySelector(
        ".inactivity-warning-dialog"
      );

      if (hasVisibleWarning) {
        return;
      }

      document
        .querySelectorAll(".p-dialog-mask")
        .forEach((element) => element.remove());

      document.body.classList.remove("p-overflow-hidden");
      document.body.style.pointerEvents = "";
      console.log("[AuthContext] Cleaned up stale dialog artifacts.");
    } catch (err) {
      console.warn("[AuthContext] Failed cleaning dialog artifacts.", err);
    }
  }, []);

  const redirectToLogout = useCallback(() => {
    console.warn("[ODP-CROSS-TAB-DEBUG] TIMEOUT_REDIRECT_LOGOUT", { origin: window.location.origin, path: window.location.pathname });
    broadcastCrossTabLogout();
    console.warn("[AuthContext] Redirecting to /logout");
    window.location.replace("/logout");
  }, []);

  const getIdlePromptAtMs = useCallback((): number => {
    return lastInteractionAtRef.current + IDLE_WARNING_AFTER_MS;
  }, []);

  const getTokenHardLogoutAtMs = useCallback((): number => {
    const effectiveRealExp = getEffectiveAccessTokenExp();
    if (!effectiveRealExp) {
      return Number.POSITIVE_INFINITY;
    }

    return effectiveRealExp * 1000;
  }, [getEffectiveAccessTokenExp]);

  const updateCountdownFromDeadline = useCallback((deadlineMs: number | null) => {
    if (!deadlineMs) {
      setCountdownSeconds(0);
      return;
    }

    const remainingSeconds = Math.max(
      0,
      Math.ceil((deadlineMs - Date.now()) / 1000)
    );
    setCountdownSeconds(remainingSeconds);
  }, []);

  const showWarningNow = useCallback(
    (reason: WarningReason, deadlineMs: number) => {
      if (!mounted.current || isCrossTabLogoutInProgress()) {
        return;
      }

      // Recheck shared activity at the final point before displaying an idle warning.
      // A queued callback must not reopen a warning for an obsolete idle period.
      if (reason === "idle-threshold") {
        const latestActivityAt = getEffectiveLastActivityAt();
        const currentPromptAt = latestActivityAt + IDLE_WARNING_AFTER_MS;
        const currentDeadline = currentPromptAt + IDLE_WARNING_COUNTDOWN_SECONDS * 1000;
        if (Date.now() < currentPromptAt || deadlineMs !== currentDeadline) {
          console.log("[AuthContext] Ignoring obsolete idle warning after shared activity.");
          return;
        }
      }

      if (showWarningRef.current && warningReasonRef.current === reason &&
          warningDeadlineMsRef.current === deadlineMs) {
        return;
      }

      console.log("[AuthContext] Showing warning", {
        reason,
        deadlineMs,
        deadlineTime: new Date(deadlineMs).toLocaleString(),
        idleWarningAfterMinutes: IDLE_WARNING_AFTER_MINUTES,
        idleCountdownMinutes: IDLE_WARNING_COUNTDOWN_MINUTES,
      });

      warningDeadlineMsRef.current = deadlineMs;
      warningReasonRef.current = reason;
      showWarningRef.current = true;
      setWarningReason(reason);
      setShowInactivityWarning(true);
      setUserChoseLogout(false);
      setIsExtendingSession(false);
      updateCountdownFromDeadline(deadlineMs);
    },
    [updateCountdownFromDeadline, getEffectiveLastActivityAt]
  );

  const scheduleIdleWarning = useCallback(() => {
    if (!isAuthenticated || showWarningRef.current || isExtendingSession) {
      return;
    }

    if (idleWarningTimeoutRef.current) {
      clearTimeout(idleWarningTimeoutRef.current);
    }

    const nowMs = Date.now();
    const idlePromptAtMs = getIdlePromptAtMs();
    const delayMs = Math.max(0, idlePromptAtMs - nowMs);

    console.log("[AuthContext] Scheduling idle warning", {
      nowMs,
      idlePromptAtMs,
      delayMs,
      at: new Date(nowMs + delayMs).toLocaleString(),
      idleWarningAfterMinutes: IDLE_WARNING_AFTER_MINUTES,
      idleCountdownMinutes: IDLE_WARNING_COUNTDOWN_MINUTES,
    });

    idleWarningTimeoutRef.current = setTimeout(() => {
      if (!mounted.current || !isAuthenticated || isExtendingSession || isCrossTabLogoutInProgress()) {
        return;
      }

      // Ignore an idle timer queued before another tab reported activity.
      // Its callback may still run after the old warning was dismissed.
      const effectiveLastActivity = getEffectiveLastActivityAt();
      const latestPromptAtMs = effectiveLastActivity + IDLE_WARNING_AFTER_MS;
      const latestDeadlineMs = latestPromptAtMs + IDLE_WARNING_COUNTDOWN_SECONDS * 1000;
      if (showWarningRef.current || Date.now() < latestPromptAtMs) {
        return;
      }

      if (Date.now() >= latestDeadlineMs) {
        console.warn("[AuthContext] Idle hard deadline reached during idle check.");
        redirectToLogout();
        return;
      }

      showWarningNow("idle-threshold", latestDeadlineMs);
    }, delayMs);
  }, [
    isAuthenticated,
    isExtendingSession,
    getIdlePromptAtMs,
    getEffectiveLastActivityAt,
    showWarningNow,
    redirectToLogout,
  ]);

  const scheduleTokenWarning = useCallback(() => {
    if (!isAuthenticated || showWarningRef.current || isExtendingSession) {
      return;
    }

    if (tokenWarningTimeoutRef.current) {
      clearTimeout(tokenWarningTimeoutRef.current);
      tokenWarningTimeoutRef.current = null;
    }

    const realExp = getEffectiveAccessTokenExp();
    const effectiveBufferedExp = getEffectiveBufferedAccessTokenExp();

    if (!realExp) {
      console.log("[AuthContext] Token warning schedule skipped. No token exp available.");
      return;
    }

    const nowMs = Date.now();
    const tokenPromptAtMs =
      realExp * 1000 - TOKEN_WARNING_BEFORE_EXP_SECONDS * 1000;
    const tokenHardLogoutAtMs = realExp * 1000;

    if (nowMs >= tokenHardLogoutAtMs) {
      console.warn(
        "[AuthContext] Real token expiry already reached before scheduling token warning.",
        {
          realExp,
          realExpTime: new Date(realExp * 1000).toLocaleString(),
          bufferedExp: effectiveBufferedExp,
          bufferedExpTime: effectiveBufferedExp
            ? new Date(effectiveBufferedExp * 1000).toLocaleString()
            : "(none)",
        }
      );
      redirectToLogout();
      return;
    }

    if (acceptedSameTokenExpRef.current === realExp) {
      const suppressUntilMs = acceptedSameTokenSuppressUntilRef.current;

      if (suppressUntilMs && nowMs < suppressUntilMs) {
        const delayMs = Math.max(0, suppressUntilMs - nowMs);

        console.log(
          "[AuthContext] Same token was accepted recently. Temporarily suppressing repeat warning for same token.",
          {
            realExp,
            realExpTime: new Date(realExp * 1000).toLocaleString(),
            bufferedExp: effectiveBufferedExp,
            bufferedExpTime: effectiveBufferedExp
              ? new Date(effectiveBufferedExp * 1000).toLocaleString()
              : "(none)",
            suppressUntilMs,
            suppressUntilTime: new Date(suppressUntilMs).toLocaleString(),
            delayMs,
          }
        );

        tokenWarningTimeoutRef.current = setTimeout(() => {
          if (!mounted.current || !isAuthenticated || isExtendingSession || isCrossTabLogoutInProgress()) {
            return;
          }

          clearSameTokenWarningSuppression();

          const latestHardLogoutAtMs = getTokenHardLogoutAtMs();

          if (Date.now() >= latestHardLogoutAtMs) {
            console.warn(
              "[AuthContext] Same-token suppression ended after token expiry. Redirecting to /logout."
            );
            redirectToLogout();
            return;
          }

          if (isRecentlyActiveForTokenWarning()) {
            const recheckDelayMs = Math.min(
              TOKEN_WARNING_RECENT_ACTIVITY_RECHECK_MS,
              Math.max(1000, latestHardLogoutAtMs - Date.now())
            );

            console.log(
              "[AuthContext] Same-token suppression ended, but user is active. Rechecking token warning later.",
              { recheckDelayMs }
            );

            tokenWarningTimeoutRef.current = setTimeout(() => {
              if (!mounted.current || !isAuthenticated || isExtendingSession || isCrossTabLogoutInProgress()) {
                return;
              }

              scheduleTokenWarning();
            }, recheckDelayMs);

            return;
          }

          console.log("[AuthContext] Same-token suppression ended. Showing warning again for same token.");
          showWarningNow("token-expiring", latestHardLogoutAtMs);
        }, delayMs);

        return;
      }

      console.log("[AuthContext] Same-token warning suppression window already expired. Allowing warning again.");
      clearSameTokenWarningSuppression();
    }

    if (nowMs >= tokenPromptAtMs) {
      if (isRecentlyActiveForTokenWarning()) {
        const recheckDelayMs = Math.min(
          TOKEN_WARNING_RECENT_ACTIVITY_RECHECK_MS,
          Math.max(1000, tokenHardLogoutAtMs - nowMs)
        );

        console.log(
          "[AuthContext] Token is expiring soon, but user is actively using the application. Suppressing prompt and rechecking.",
          {
            realExp,
            realExpTime: new Date(realExp * 1000).toLocaleString(),
            bufferedExp: effectiveBufferedExp,
            bufferedExpTime: effectiveBufferedExp
              ? new Date(effectiveBufferedExp * 1000).toLocaleString()
              : "(none)",
            recheckDelayMs,
          }
        );

        tokenWarningTimeoutRef.current = setTimeout(() => {
          if (!mounted.current || !isAuthenticated || isExtendingSession || isCrossTabLogoutInProgress()) {
            return;
          }

          scheduleTokenWarning();
        }, recheckDelayMs);

        return;
      }

      console.log(
        "[AuthContext] Token already inside warning window. Showing prompt now.",
        {
          realExp,
          realExpTime: new Date(realExp * 1000).toLocaleString(),
          bufferedExp: effectiveBufferedExp,
          bufferedExpTime: effectiveBufferedExp
            ? new Date(effectiveBufferedExp * 1000).toLocaleString()
            : "(none)",
        }
      );
      showWarningNow("token-expiring", tokenHardLogoutAtMs);
      return;
    }

    const delayMs = Math.max(0, tokenPromptAtMs - nowMs);

    console.log("[AuthContext] Scheduling token warning", {
      nowMs,
      realExp,
      realExpTime: new Date(realExp * 1000).toLocaleString(),
      bufferedExp: effectiveBufferedExp,
      bufferedExpTime: effectiveBufferedExp
        ? new Date(effectiveBufferedExp * 1000).toLocaleString()
        : "(none)",
      tokenPromptAtMs,
      tokenHardLogoutAtMs,
      delayMs,
      at: new Date(nowMs + delayMs).toLocaleString(),
    });

    tokenWarningTimeoutRef.current = setTimeout(() => {
      if (!mounted.current || !isAuthenticated || isExtendingSession || isCrossTabLogoutInProgress()) {
        return;
      }

      const latestHardLogoutAtMs = getTokenHardLogoutAtMs();

      if (Date.now() >= latestHardLogoutAtMs) {
        console.warn("[AuthContext] Token hard deadline reached during token check.");
        redirectToLogout();
        return;
      }

      if (isRecentlyActiveForTokenWarning()) {
        const recheckDelayMs = Math.min(
          TOKEN_WARNING_RECENT_ACTIVITY_RECHECK_MS,
          Math.max(1000, latestHardLogoutAtMs - Date.now())
        );

        console.log(
          "[AuthContext] Token warning reached, but user is active. Rechecking instead of showing prompt.",
          { recheckDelayMs }
        );

        tokenWarningTimeoutRef.current = setTimeout(() => {
          if (!mounted.current || !isAuthenticated || isExtendingSession || isCrossTabLogoutInProgress()) {
            return;
          }

          scheduleTokenWarning();
        }, recheckDelayMs);

        return;
      }

      showWarningNow("token-expiring", latestHardLogoutAtMs);
    }, delayMs);
  }, [
    isAuthenticated,
    isExtendingSession,
    getEffectiveAccessTokenExp,
    getEffectiveBufferedAccessTokenExp,
    getTokenHardLogoutAtMs,
    showWarningNow,
    redirectToLogout,
    clearSameTokenWarningSuppression,
    isRecentlyActiveForTokenWarning,
  ]);

  const rescheduleWarnings = useCallback(
    (reason: string) => {
      console.log("[AuthContext] Rescheduling warnings", { reason });

      clearAllTimers();

      if (!isAuthenticated || showWarningRef.current || isExtendingSession || isCrossTabLogoutInProgress()) {
        return;
      }

      scheduleIdleWarning();
      scheduleTokenWarning();
    },
    [
      clearAllTimers,
      isAuthenticated,
      isExtendingSession,
      scheduleIdleWarning,
      scheduleTokenWarning,
    ]
  );

  useEffect(() => {
    mounted.current = true;

    const initialize = async () => {
      try {
        console.log("[AuthContext] Checking existing session...");
        console.log("[AuthContext] Inactivity timer configuration:", {
          idleWarningAfterMinutes: IDLE_WARNING_AFTER_MINUTES,
          idleCountdownMinutes: IDLE_WARNING_COUNTDOWN_MINUTES,
          rawAuthProvider: authProvider,
          normalizedAuthProvider,
        });

        const active = await authService.hasActiveSession();
        console.log("[AuthContext] Session active?", active);

        if (!mounted.current || isCrossTabLogoutInProgress()) {
          return;
        }

        if (!active) {
          if (normalizedAuthProvider === "siteminder") {
            clearClientAuthState("initialize-no-active-siteminder-session");
            console.log(
              "[AuthContext] No active SiteMinder session. Skipping stale rehydration."
            );
          } else {
            const storedUser = sessionStorage.getItem("appUser");
            const storedExp = readStoredAccessTokenExp();

            if (storedUser && storedExp) {
              const parsedUser = JSON.parse(storedUser) as AppUser;
              setAppUser(parsedUser);
              setAccessTokenExp(storedExp);
              setIsUserValidated(false);
              console.log(
                "[AuthContext] Rehydrated appUser + exp from sessionStorage"
              );
            } else {
              console.log("[AuthContext] No session and nothing to rehydrate");
              setAppUser(null);
              setAccessTokenExp(null);
              setIsUserValidated(false);
            }

            setIsAuthenticated(false);
          }

          return;
        }

        setIsAuthenticated(true);
        clearRenewBlockState();

        const user = await authService.getUser();
        if (!mounted.current || isCrossTabLogoutInProgress()) return;
        if (!user) {
          console.warn(
            "[AuthContext] Active session reported, but getUser() returned null. Treating as invalid session."
          );
          clearClientAuthState("initialize-active-session-but-user-null");
          return;
        }

        setAppUser(user);
        setIsUserValidated(true);
        sessionStorage.setItem("appUser", JSON.stringify(user));

        const now = Date.now();
        lastInteractionAtRef.current = now;
        writeSharedLastActivityAt(now);

        const tokenFromUser = getAccessTokenFromUser(user);
        if (tokenFromUser) {
          syncAccessTokenExpFromToken(tokenFromUser);
        } else {
          const storedExp = readStoredAccessTokenExp();
          setAccessTokenExp(storedExp);
        }
      } catch (err) {
        console.error("[AuthContext] initialize() error:", err);
        if (mounted.current) {
          clearClientAuthState("initialize-exception");
        }
      }
    };

    initialize();

    return () => {
      mounted.current = false;
    };
  }, [
    authService,
    authProvider,
    normalizedAuthProvider,
    clearRenewBlockState,
    clearClientAuthState,
    getAccessTokenFromUser,
    readStoredAccessTokenExp,
    syncAccessTokenExpFromToken,
    writeSharedLastActivityAt,
  ]);

  // A peer tab may refresh its OIDC user while this tab is idle.
  useEffect(() => {
    if (normalizedAuthProvider !== "siteminder") return;
    const onPeerRenew = () => {
      if (isCrossTabLogoutInProgress()) return;
      const token = sessionStorage.getItem("access_token");
      if (!token) return;
      try {
        const decoded: { exp: number } = jwtDecode(token);
        if (decoded.exp <= Date.now() / 1000) return;
        setAccessTokenExp(decoded.exp);
        clearRenewBlockState();
        // A renewed token is not proof of user activity. Keep an idle warning
        // visible until the peer activity signal resets its deadline.
        if (showWarningRef.current && warningReasonRef.current === "idle-threshold") {
          return;
        }
        showWarningRef.current = false;
        clearWarningState();
        window.setTimeout(() => rescheduleWarnings("peer-session-renewed"), 0);
      } catch (error) {
        console.warn("[AuthContext] Invalid peer token expiration", error);
      }
    };
    window.addEventListener("odp:peer-session-renewed", onPeerRenew);
    return () => window.removeEventListener("odp:peer-session-renewed", onPeerRenew);
  }, [normalizedAuthProvider, clearRenewBlockState, clearWarningState, rescheduleWarnings]);

  useEffect(() => {
    if (!isAuthenticated || normalizedAuthProvider !== "siteminder") return;
    return subscribeCrossTabActivity((timestamp) => {
      if (isCrossTabLogoutInProgress() || timestamp <= lastInteractionAtRef.current) return;
      lastInteractionAtRef.current = timestamp;

      // An activity signal from another authenticated tab resets the shared
      // inactivity timer, including when this tab already shows an idle prompt.
      // Do not dismiss token-expiry prompts based on activity alone.
      if (showWarningRef.current) {
        if (warningReasonRef.current !== "idle-threshold") return;
        showWarningRef.current = false;
        clearAllTimers();
        clearWarningState();
        window.setTimeout(() => rescheduleWarnings("peer-idle-warning-dismissed"), 0);
        return;
      }
      rescheduleWarnings("peer-activity");
    });
  }, [isAuthenticated, normalizedAuthProvider, clearAllTimers, clearWarningState, rescheduleWarnings]);

  // An idle timeout or explicit logout in any tab ends the ODP session in all tabs.
  useEffect(() => {
    if (normalizedAuthProvider !== "siteminder") return;
    console.warn("[ODP-CROSS-TAB-DEBUG] AUTH_LOGOUT_LISTENER_REGISTERED", { origin: window.location.origin, path: window.location.pathname });
    return subscribeCrossTabLogout(() => {
      console.warn("[ODP-CROSS-TAB-DEBUG] AUTH_PEER_LOGOUT_HANDLER_ENTERED", { origin: window.location.origin, path: window.location.pathname });
      clearAllTimers();
      showWarningRef.current = false;
      clearWarningState();
      clearClientAuthState("peer-logout");
      // Use the same SiteMinder signout path as the initiating tab.
      // Do not remove the OIDC user first: logout needs its id_token_hint.
      void (async () => {
        try {
          console.warn("[ODP-CROSS-TAB-DEBUG] AUTH_PEER_SITEMINDER_SIGNOUT_STARTED");
          await authService.logout();
          console.warn("[ODP-CROSS-TAB-DEBUG] AUTH_PEER_SITEMINDER_SIGNOUT_COMPLETED");
        } catch (error) {
          console.error("[ODP-CROSS-TAB-DEBUG] AUTH_PEER_SITEMINDER_SIGNOUT_FAILED", error);
          window.location.replace("/logout");
        }
      })();
    });
  }, [normalizedAuthProvider, clearAllTimers, clearWarningState, clearClientAuthState, authService]);

  useUserActivityTokenRefresh(accessTokenExp, setAppUser, setAccessTokenExp);

  const signIn = useCallback(
    async (username?: string, password?: string) => {
      console.log("[AuthContext] signIn()", normalizedAuthProvider);

      if (normalizedAuthProvider === "siteminder") {
        await authService.login();
        return;
      }

      if (!username || !password) {
        throw new Error("Username and password required for non-siteminder login.");
      }

      await authService.login(username, password);

      if (!mounted.current) return;

      const user = await authService.getUser();
      if (!user) {
        console.warn("[AuthContext] signIn(): getUser() returned null");
        setIsUserValidated(false);
        return;
      }

      clearRenewBlockState();
      setAppUser(user);
      setIsUserValidated(true);
      sessionStorage.setItem("appUser", JSON.stringify(user));
      setIsAuthenticated(true);

      const now = Date.now();
      lastInteractionAtRef.current = now;
      writeSharedLastActivityAt(now);

      const tokenFromUser = getAccessTokenFromUser(user);
      if (tokenFromUser) {
        syncAccessTokenExpFromToken(tokenFromUser);
      } else {
        const storedExp = readStoredAccessTokenExp();
        setAccessTokenExp(storedExp);
      }
    },
    [
      normalizedAuthProvider,
      authService,
      clearRenewBlockState,
      getAccessTokenFromUser,
      readStoredAccessTokenExp,
      syncAccessTokenExpFromToken,
      writeSharedLastActivityAt,
    ]
  );

  const signOut = useCallback(async () => {
    console.warn("[ODP-CROSS-TAB-DEBUG] SIGNOUT_ENTERED", { origin: window.location.origin, path: window.location.pathname, mounted: mounted.current, logoutInProgress: isCrossTabLogoutInProgress() });
    broadcastCrossTabLogout();
    console.warn("[ODP-CROSS-TAB-DEBUG] SIGNOUT_BROADCAST_RETURNED", { logoutInProgress: isCrossTabLogoutInProgress() });
    await waitForCrossTabLogoutAcknowledgement();
    console.log("[AuthContext] Signing out...");

    if (!mounted.current) return;

    try {
      console.log("[AuthContext] Calling authService.logout()...");
      await authService.logout();
      console.log("[AuthContext] authService.logout() completed.");
    } catch (err) {
      console.error("[AuthContext] logout error:", err);
    }

    clearAllTimers();
    warningDeadlineMsRef.current = null;
    clearSameTokenWarningSuppression();
    clearRenewBlockState();

    setIsAuthenticated(false);
    setAppUser(null);
    setUser(null);
    setIsUserValidated(false);
    setAccessTokenExp(null);
    setShowInactivityWarning(false);
    setCountdownSeconds(0);
    setUserChoseLogout(false);
    setIsExtendingSession(false);
    setWarningReason(null);

    try {
      sessionStorage.removeItem("appUser");
      sessionStorage.removeItem("accessTokenExp");
      sessionStorage.removeItem("authenticated");
      sessionStorage.removeItem("access_token");
      sessionStorage.removeItem("id_token");
      sessionStorage.removeItem("authorizationToken");
      sessionStorage.removeItem("authorizationTokenExp");
      sessionStorage.removeItem("loginDenied");
      
      console.log("[AuthContext] Cleared sessionStorage auth state.");
    } catch (err) {
      console.error("[AuthContext] Error clearing sessionStorage:", err);
    }

    cleanupStaleDialogArtifacts();
  }, [
    authService,
    clearAllTimers,
    clearSameTokenWarningSuppression,
    clearRenewBlockState,
    cleanupStaleDialogArtifacts,
  ]);

  const hasActiveSession = useCallback(async (): Promise<boolean> => {
    try {
      const active = await authService.hasActiveSession();
      if (!mounted.current) return active;

      setIsAuthenticated(active);

      if (active) {
        clearRenewBlockState();

        const user = await authService.getUser();
        if (!user) {
          console.warn(
            "[AuthContext] hasActiveSession(): active=true but getUser() returned null. Treating as invalid session."
          );
          clearClientAuthState("hasActiveSession-active-true-user-null");
          return false;
        }

        setAppUser(user);
        setIsUserValidated(true);
        sessionStorage.setItem("appUser", JSON.stringify(user));

        const tokenFromUser = getAccessTokenFromUser(user);
        if (tokenFromUser) {
          syncAccessTokenExpFromToken(tokenFromUser);
        } else {
          const storedExp = readStoredAccessTokenExp();
          setAccessTokenExp(storedExp);
        }

        return true;
      }

      clearClientAuthState("hasActiveSession-false");
      return false;
    } catch (err) {
      console.error("[AuthContext] hasActiveSession() error:", err);
      clearClientAuthState("hasActiveSession-exception");
      return false;
    }
  }, [
    authService,
    clearRenewBlockState,
    clearClientAuthState,
    getAccessTokenFromUser,
    readStoredAccessTokenExp,
    syncAccessTokenExpFromToken,
  ]);

  const forceRefreshSiteminderSession = useCallback(async (): Promise<boolean> => {
    const getRenewErrorText = (err: unknown): string => {
      if (err instanceof Error) {
        return err.message || "";
      }

      try {
        return JSON.stringify(err);
      } catch {
        return String(err);
      }
    };

    const isInvalidRefreshTokenError = (err: unknown): boolean => {
      const text = getRenewErrorText(err).toLowerCase();
      return (
        text.includes("invalid_refresh_token") ||
        text.includes("invalid refresh_token") ||
        text.includes("invalid_grant")
      );
    };

    const trySilentRenewOnce = async (
      currentPreviousExp: number | null,
      label: string
    ): Promise<{ user: User | null; refreshedExp: number | null }> => {
      const nextUser = await silentUserManager.signinSilent();
      const nextRefreshedExp = syncAccessTokenExpFromToken(
        nextUser?.access_token || null
      );

      console.log(`[AuthContext] ${label}:`, {
        previousExp: currentPreviousExp,
        previousExpTime: currentPreviousExp
          ? new Date(currentPreviousExp * 1000).toLocaleString()
          : "(none)",
        refreshedExp: nextRefreshedExp,
        refreshedExpTime: nextRefreshedExp
          ? new Date(nextRefreshedExp * 1000).toLocaleString()
          : "(none)",
        movedForward:
          currentPreviousExp && nextRefreshedExp
            ? nextRefreshedExp > currentPreviousExp
            : null,
      });

      return { user: nextUser, refreshedExp: nextRefreshedExp };
    };

    try {
      console.log("[AuthContext] Forcing SiteMinder silent renew from warning dialog.");

      const previousExp = getEffectiveAccessTokenExp();
      console.log("[AuthContext] Silent renew starting with previous exp:", {
        previousExp,
        previousExpTime: previousExp
          ? new Date(previousExp * 1000).toLocaleString()
          : "(none)",
      });

      let { user, refreshedExp } = await trySilentRenewOnce(
        previousExp,
        "Silent renew first result"
      );

      if (previousExp && refreshedExp && refreshedExp <= previousExp) {
        console.warn(
          "[AuthContext] signinSilent returned same/older token exp. Clearing cached OIDC state and retrying once.",
          {
            previousExp,
            previousExpTime: new Date(previousExp * 1000).toLocaleString(),
            refreshedExp,
            refreshedExpTime: new Date(refreshedExp * 1000).toLocaleString(),
          }
        );

        // Keep the existing OIDC user while retrying. Another tab may have
        // successfully renewed it; removeUser would destroy that state.

        const retryResult = await trySilentRenewOnce(
          previousExp,
          "Silent renew retry result"
        );
        user = retryResult.user;
        refreshedExp = retryResult.refreshedExp;
      }

      if (!user) {
        console.warn("[AuthContext] signinSilent returned null user during extendSession.");
        setIsUserValidated(false);
        return false;
      }

      if (!refreshedExp) {
        console.error("[AuthContext] Failed to decode refreshed SiteMinder token exp.");
        setIsUserValidated(false);
        return false;
      }

      let refreshedBufferedSecondsLeft = getSecondsLeftFromExp(refreshedExp);
      let refreshedRealSecondsLeft = getRealSecondsLeftFromExp(refreshedExp);

      console.log("[AuthContext] Silent renew final token check:", {
        previousExp,
        previousExpTime: previousExp
          ? new Date(previousExp * 1000).toLocaleString()
          : "(none)",
        refreshedExp,
        refreshedExpTime: refreshedExp
          ? new Date(refreshedExp * 1000).toLocaleString()
          : "(none)",
        refreshedBufferedSecondsLeft,
        refreshedRealSecondsLeft,
        movedForward:
          previousExp && refreshedExp ? refreshedExp > previousExp : null,
      });

      if (
        previousExp &&
        refreshedExp <= previousExp &&
        (refreshedRealSecondsLeft === null ||
          refreshedRealSecondsLeft <= MIN_VALID_SECONDS_AFTER_EXTEND)
      ) {
        console.warn(
          "[AuthContext] Silent renew returned same/older token too close to real expiry. Waiting briefly and retrying before treating as failure.",
          {
            previousExp,
            previousExpTime: new Date(previousExp * 1000).toLocaleString(),
            refreshedExp,
            refreshedExpTime: new Date(refreshedExp * 1000).toLocaleString(),
            refreshedBufferedSecondsLeft,
            refreshedRealSecondsLeft,
            retryDelayMs: EXTEND_SAME_TOKEN_RETRY_DELAY_MS,
            maxRetries: EXTEND_SAME_TOKEN_MAX_RETRIES,
          }
        );

        let advanced = false;

        for (let attempt = 1; attempt <= EXTEND_SAME_TOKEN_MAX_RETRIES; attempt++) {
          await wait(EXTEND_SAME_TOKEN_RETRY_DELAY_MS);

          const latestBeforeRetryExp = getEffectiveAccessTokenExp();
          const latestBeforeRetryRealSecondsLeft =
            getRealSecondsLeftFromExp(latestBeforeRetryExp);

          if (
            latestBeforeRetryRealSecondsLeft !== null &&
            latestBeforeRetryRealSecondsLeft <= 0
          ) {
            console.error("[AuthContext] Real token expiry reached while waiting for extend-session grace retry.");
            setIsUserValidated(false);
            return false;
          }

          console.log("[AuthContext] Extend-session grace retry starting.", {
            attempt,
            latestBeforeRetryExp,
            latestBeforeRetryExpTime: latestBeforeRetryExp
              ? new Date(latestBeforeRetryExp * 1000).toLocaleString()
              : "(none)",
            latestBeforeRetryRealSecondsLeft,
          });

          const retryAttempt = await trySilentRenewOnce(
            previousExp,
            `Extend-session grace retry result ${attempt}`
          );

          if (retryAttempt.user) {
            user = retryAttempt.user;
          }

          if (retryAttempt.refreshedExp) {
            refreshedExp = retryAttempt.refreshedExp;
            refreshedBufferedSecondsLeft = getSecondsLeftFromExp(refreshedExp);
            refreshedRealSecondsLeft = getRealSecondsLeftFromExp(refreshedExp);
          }

          if (previousExp && refreshedExp && refreshedExp > previousExp) {
            advanced = true;
            console.log("[AuthContext] Extend-session grace retry succeeded with newer token.", {
              attempt,
              refreshedExp,
              refreshedExpTime: new Date(refreshedExp * 1000).toLocaleString(),
              refreshedBufferedSecondsLeft,
              refreshedRealSecondsLeft,
            });
            break;
          }
        }

        if (
          !advanced &&
          previousExp &&
          refreshedExp <= previousExp &&
          (refreshedRealSecondsLeft === null ||
            refreshedRealSecondsLeft <= MIN_VALID_SECONDS_AFTER_EXTEND)
        ) {
          console.error(
            "[AuthContext] Silent renew still returned same/older token and real expiry is too close after grace retries. Treating as failure.",
            {
              previousExp,
              previousExpTime: new Date(previousExp * 1000).toLocaleString(),
              refreshedExp,
              refreshedExpTime: refreshedExp
                ? new Date(refreshedExp * 1000).toLocaleString()
                : "(none)",
              refreshedBufferedSecondsLeft,
              refreshedRealSecondsLeft,
            }
          );
          setIsUserValidated(false);
          return false;
        }
      }

      if (!user) {
        console.error("[AuthContext] Silent renew flow ended without a valid user after retry handling.");
        setIsUserValidated(false);
        return false;
      }

      if (previousExp && refreshedExp <= previousExp) {
        console.warn(
          "[AuthContext] Silent renew returned same token exp, but real token is still valid. Accepting current token and keeping session alive temporarily.",
          {
            previousExp,
            previousExpTime: new Date(previousExp * 1000).toLocaleString(),
            refreshedExp,
            refreshedExpTime: new Date(refreshedExp * 1000).toLocaleString(),
            refreshedBufferedSecondsLeft,
            refreshedRealSecondsLeft,
          }
        );
      }

      const refreshedAppUser = preserveRolesAfterRefresh(buildAppUserFromOidcUser(user));

      sessionStorage.setItem("access_token", user.access_token);
      sessionStorage.setItem("id_token", user.id_token || "");
      sessionStorage.setItem("appUser", JSON.stringify(refreshedAppUser));
      sessionStorage.setItem("authenticated", "true");

      try {
        console.log("[AuthContext] Refreshing backend session with renewed SiteMinder token.");
        await httpService.refreshBackendSessionWithToken(user.access_token);
      } catch (backendRefreshErr) {
        console.error("[AuthContext] Backend session refresh failed after silent renew", backendRefreshErr);
        setIsUserValidated(false);
        return false;
      }

      clearRenewBlockState();
      setAppUser(refreshedAppUser);
      setIsAuthenticated(true);
      setIsUserValidated(true);

      console.log("[AuthContext] Forced SiteMinder refresh completed successfully.", {
        previousExp,
        previousExpTime: previousExp
          ? new Date(previousExp * 1000).toLocaleString()
          : "(none)",
        refreshedExp,
        refreshedExpTime: refreshedExp
          ? new Date(refreshedExp * 1000).toLocaleString()
          : "(none)",
        refreshedBufferedSecondsLeft,
        refreshedRealSecondsLeft,
        movedForward:
          previousExp && refreshedExp ? refreshedExp > previousExp : null,
      });

      return true;
    } catch (err) {
      console.error("[AuthContext] forceRefreshSiteminderSession failed", err);

      if (isInvalidRefreshTokenError(err)) {
        console.warn("[AuthContext] Silent renew failed because refresh token is invalid.");
      }

      setIsUserValidated(false);
      return false;
    }
  }, [
    silentUserManager,
    clearRenewBlockState,
    syncAccessTokenExpFromToken,
    buildAppUserFromOidcUser,
    preserveRolesAfterRefresh,
    getEffectiveAccessTokenExp,
    getSecondsLeftFromExp,
    getRealSecondsLeftFromExp,
    wait,
  ]);

  const isSuperAdministrator = useCallback((): boolean => {
    return !!appUser?.groups?.some(
      (g) => g?.toLowerCase() === Constants.SUPER_ADMIN_GROUP_NAME?.toLowerCase()
    );
  }, [appUser]);

  const isAdministrator = useCallback((): boolean => {
    return (
      !!appUser?.groups?.some(
        (g) => g?.toLowerCase() === Constants.ADMIN_GROUP_NAME?.toLowerCase()
      ) || isSuperAdministrator()
    );
  }, [appUser, isSuperAdministrator]);

  const isFinalReviewer = useCallback((): boolean => {
    return (
      appUser?.groups?.map((g: any) => g?.toLowerCase()).includes(
        Constants.FINAL_REVIEWER_GROUP_NAME?.toLowerCase()
      ) || false
    );
  }, [appUser]);

  const isSmeReviewer = useCallback((): boolean => {
    return (
      appUser?.groups?.map((g: any) => g?.toLowerCase()).includes(
        Constants.SME_REVIEWER_GROUP_NAME?.toLowerCase()
      ) || false
    );
  }, [appUser]);

  const isInitialReviewer = useCallback((): boolean => {
    return (
      appUser?.groups?.map((g: any) => g?.toLowerCase()).includes(
        Constants.INITIAL_REVIEWER_GROUP_NAME?.toLowerCase()
      ) || false
    );
  }, [appUser]);

  const appUserWithReviewerType = useMemo(() => {
    if (!appUser) return null;

    let reviewerType: string | undefined = undefined;

    if (isAdministrator()) {
      reviewerType = Constants.ADMIN_NAME;
    } else if (isFinalReviewer()) {
      reviewerType = Constants.FINAL_REVIEWER_NAME;
    } else if (isSmeReviewer()) {
      reviewerType = Constants.SME_REVIEWER_NAME;
    } else if (isInitialReviewer()) {
      reviewerType = Constants.INITIAL_REVIEWER_NAME;
    }

    return {
      ...appUser,
      reviewerType,
    };
  }, [
    appUser,
    isAdministrator,
    isFinalReviewer,
    isSmeReviewer,
    isInitialReviewer,
  ]);

  const currentUserRoles = useCallback((): string[] => {
    return (appUserWithReviewerType?.groups as string[]) || [];
  }, [appUserWithReviewerType]);

  const isAuthorizedUser = useCallback(
    (allowedRoles: string[]): boolean => {
      if (!allowedRoles || allowedRoles.length === 0) return true;

      const roles = currentUserRoles();
      if (!roles || roles.length === 0) return false;

      return allowedRoles.some((r) => roles.includes(r));
    },
    [currentUserRoles]
  );

  useEffect(() => {
    userChoseLogoutRef.current = userChoseLogout;
  }, [userChoseLogout]);

  useEffect(() => {
    if (
      acceptedSameTokenExpRef.current &&
      accessTokenExp &&
      acceptedSameTokenExpRef.current !== accessTokenExp
    ) {
      console.log("[AuthContext] New token expiry detected. Clearing same-token accepted marker.", {
        oldAcceptedExp: acceptedSameTokenExpRef.current,
        newExp: accessTokenExp,
      });
      clearSameTokenWarningSuppression();
    }
  }, [accessTokenExp, clearSameTokenWarningSuppression]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      warningDeadlineMsRef.current = null;
      return;
    }

    rescheduleWarnings("auth-state-change");
  }, [
    isAuthenticated,
    accessTokenExp,
    isExtendingSession,
    clearAllTimers,
    rescheduleWarnings,
  ]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const handleUserActivity = (event: Event) => {
      if (
        showWarningRef.current ||
        userChoseLogoutRef.current ||
        isExtendingSession
      ) {
        console.log("[AuthContext] Ignoring activity while warning/logout/extend is active.", {
          eventType: event.type,
        });
        return;
      }

      const target = event.target;
      if (target && target instanceof Element) {
        const dialogElement =
          target.closest(".inactivity-warning-dialog") ||
          target.closest(".p-dialog") ||
          target.closest("[role='dialog']") ||
          target.closest(".p-component-overlay");

        if (dialogElement) {
          console.log("[AuthContext] Ignoring dialog interaction:", event.type);
          return;
        }
      }

      const now = Date.now();
      lastInteractionAtRef.current = now;
      writeSharedLastActivityAt(now);

      console.log("[AuthContext] Real user activity detected:", event.type, {
        at: new Date(lastInteractionAtRef.current).toLocaleString(),
      });

      rescheduleWarnings(`activity-${event.type}`);
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, handleUserActivity, true);
    });

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        document.removeEventListener(eventName, handleUserActivity, true);
      });
    };
  }, [
    isAuthenticated,
    isExtendingSession,
    rescheduleWarnings,
    writeSharedLastActivityAt,
  ]);

  useEffect(() => {
    let countdownInterval: ReturnType<typeof setInterval> | undefined;

    if ((showInactivityWarning || userChoseLogout) && !isExtendingSession) {
      countdownInterval = setInterval(() => {
        const deadlineMs = warningDeadlineMsRef.current;

        if (!deadlineMs) {
          setCountdownSeconds(0);
          return;
        }

        const remainingSeconds = Math.max(
          0,
          Math.ceil((deadlineMs - Date.now()) / 1000)
        );

        if (remainingSeconds <= 0) {
          // A second tab may have extended the shared inactivity deadline
          // while this tab's countdown was already running.
          if (warningReasonRef.current === "idle-threshold" &&
              Date.now() - getEffectiveLastActivityAt() < IDLE_WARNING_AFTER_MS) {
            showWarningRef.current = false;
            clearAllTimers();
            clearWarningState();
            window.setTimeout(() => rescheduleWarnings("peer-idle-deadline-updated"), 0);
            return;
          }
          clearInterval(countdownInterval);
          console.warn("[AuthContext] Warning timer reached zero. Redirecting to /logout.");
          redirectToLogout();
          return;
        }

        setCountdownSeconds(remainingSeconds);
      }, 1000);
    }

    return () => {
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [
    showInactivityWarning,
    userChoseLogout,
    isExtendingSession,
    redirectToLogout,
    getEffectiveLastActivityAt,
    clearAllTimers,
    clearWarningState,
    rescheduleWarnings,
  ]);

  useEffect(() => {
    if (showInactivityWarning) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      cleanupStaleDialogArtifacts();
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [showInactivityWarning, cleanupStaleDialogArtifacts]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const handleTokenWarningRequired = (event: Event) => {
      if (!mounted.current || !isAuthenticated || isExtendingSession || isCrossTabLogoutInProgress()) {
        return;
      }

      if (showWarningRef.current) {
        return;
      }

      const customEvent = event as CustomEvent<{
        secondsLeft?: number;
        reason?: string;
      }>;

      const latestHardLogoutAtMs = getTokenHardLogoutAtMs();

      if (Date.now() >= latestHardLogoutAtMs) {
        console.warn("[AuthContext] Received token warning required event, but token is already expired.");
        redirectToLogout();
        return;
      }

      console.warn("[AuthContext] Received token warning required event.", {
        secondsLeft: customEvent.detail?.secondsLeft,
        reason: customEvent.detail?.reason,
        hardLogoutAt: new Date(latestHardLogoutAtMs).toLocaleString(),
      });

      showWarningNow("token-expiring", latestHardLogoutAtMs);
    };

    const handleTokenExpired = (event: Event) => {
      const customEvent = event as CustomEvent<{ reason?: string }>;

      console.error("[AuthContext] Received token expired event.", {
        reason: customEvent.detail?.reason,
      });

      redirectToLogout();
    };

    window.addEventListener(
      "auth:token-warning-required",
      handleTokenWarningRequired as EventListener
    );
    window.addEventListener(
      "auth:token-expired",
      handleTokenExpired as EventListener
    );

    return () => {
      window.removeEventListener(
        "auth:token-warning-required",
        handleTokenWarningRequired as EventListener
      );
      window.removeEventListener(
        "auth:token-expired",
        handleTokenExpired as EventListener
      );
    };
  }, [
    isAuthenticated,
    isExtendingSession,
    getTokenHardLogoutAtMs,
    redirectToLogout,
    showWarningNow,
  ]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (!mounted.current || !isAuthenticated || isExtendingSession || isCrossTabLogoutInProgress()) {
        return;
      }

      if (showWarningRef.current) {
        return;
      }

      const realExp = getEffectiveAccessTokenExp();
      const realSecondsLeft = getRealSecondsLeftFromExp(realExp);

      if (realSecondsLeft === null) {
        return;
      }

      if (realSecondsLeft <= 0) {
        console.error("[AuthContext] Token watchdog detected real token expiry.");
        redirectToLogout();
        return;
      }

      if (
        realSecondsLeft <= TOKEN_WARNING_BEFORE_EXP_SECONDS &&
        !isRecentlyActiveForTokenWarning()
      ) {
        const latestHardLogoutAtMs = getTokenHardLogoutAtMs();

        console.warn("[AuthContext] Token watchdog is forcing token warning.", {
          realSecondsLeft,
          latestHardLogoutAtMs,
          latestHardLogoutTime: new Date(latestHardLogoutAtMs).toLocaleString(),
        });

        showWarningNow("token-expiring", latestHardLogoutAtMs);
      }
    }, TOKEN_WATCHDOG_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    isAuthenticated,
    isExtendingSession,
    getEffectiveAccessTokenExp,
    getRealSecondsLeftFromExp,
    getTokenHardLogoutAtMs,
    isRecentlyActiveForTokenWarning,
    redirectToLogout,
    showWarningNow,
  ]);

  const handleExtendSession = async () => {
    if (isExtendingSession) {
      return;
    }

    const previousExp = getEffectiveAccessTokenExp();

    setIsExtendingSession(true);
    clearAllTimers();
    warningDeadlineMsRef.current = null;
    showWarningRef.current = false;
    setShowInactivityWarning(false);
    setUserChoseLogout(false);
    setCountdownSeconds(0);

    try {
      console.log("[AuthContext] User selected stay logged in. Refreshing session.", {
        rawAuthProvider: authProvider,
        normalizedAuthProvider,
      });

      let sessionOk = false;

      if (normalizedAuthProvider === "siteminder") {
        sessionOk = await forceRefreshSiteminderSession();
      } else {
        sessionOk = await hasActiveSession();
      }

      if (!sessionOk) {
        console.warn("[AuthContext] Session refresh failed. Logging out.");
        await signOut();
        return;
      }

      const latestToken = sessionStorage.getItem("access_token");
      if (!latestToken) {
        console.error("[AuthContext] No access token found after successful refresh.");
        await signOut();
        return;
      }

      const decodedLatest: { exp: number } = jwtDecode(latestToken);
      const latestBufferedSecondsLeft = getSecondsLeftFromExp(decodedLatest.exp);
      const latestRealSecondsLeft = getRealSecondsLeftFromExp(decodedLatest.exp);

      setAccessTokenExp(decodedLatest.exp);
      setIsUserValidated(true);
      sessionStorage.setItem("accessTokenExp", String(decodedLatest.exp));
      clearRenewBlockState();

      console.log("[AuthContext] Extend session verification:", {
        previousExp,
        previousExpTime: previousExp
          ? new Date(previousExp * 1000).toLocaleString()
          : "(none)",
        latestExp: decodedLatest.exp,
        latestExpTime: new Date(decodedLatest.exp * 1000).toLocaleString(),
        latestBufferedSecondsLeft,
        latestRealSecondsLeft,
        movedForward: previousExp ? decodedLatest.exp > previousExp : null,
      });

      if (normalizedAuthProvider === "siteminder") {
        if (latestRealSecondsLeft === null || latestRealSecondsLeft <= 0) {
          console.error(
            "[AuthContext] Refreshed SiteMinder token is already really expired after extend-session flow. Treating extend as failed.",
            {
              previousExp,
              previousExpTime: previousExp
                ? new Date(previousExp * 1000).toLocaleString()
                : "(none)",
              latestExp: decodedLatest.exp,
              latestExpTime: new Date(decodedLatest.exp * 1000).toLocaleString(),
              latestBufferedSecondsLeft,
              latestRealSecondsLeft,
            }
          );
          await signOut();
          return;
        }

        if (
          previousExp &&
          decodedLatest.exp <= previousExp &&
          latestRealSecondsLeft !== null &&
          latestRealSecondsLeft <= MIN_VALID_SECONDS_AFTER_EXTEND
        ) {
          console.error(
            "[AuthContext] SiteMinder manual extend kept same/older token too close to real expiry. Treating extend as failed.",
            {
              previousExp,
              previousExpTime: new Date(previousExp * 1000).toLocaleString(),
              latestExp: decodedLatest.exp,
              latestExpTime: new Date(decodedLatest.exp * 1000).toLocaleString(),
              latestBufferedSecondsLeft,
              latestRealSecondsLeft,
            }
          );
          await signOut();
          return;
        }
      } else {
        if (latestBufferedSecondsLeft === null || latestBufferedSecondsLeft <= 0) {
          console.error(
            "[AuthContext] Refreshed token is already expired in buffered app-time after extend-session flow. Treating extend as failed.",
            {
              previousExp,
              previousExpTime: previousExp
                ? new Date(previousExp * 1000).toLocaleString()
                : "(none)",
              latestExp: decodedLatest.exp,
              latestExpTime: new Date(decodedLatest.exp * 1000).toLocaleString(),
              latestBufferedSecondsLeft,
              latestRealSecondsLeft,
            }
          );
          await signOut();
          return;
        }

        if (
          previousExp &&
          decodedLatest.exp <= previousExp &&
          latestBufferedSecondsLeft !== null &&
          latestBufferedSecondsLeft <= MIN_VALID_SECONDS_AFTER_EXTEND
        ) {
          console.error(
            "[AuthContext] Renewed token exp did not move forward and buffered token is too close to expiry. Treating extend as failed.",
            {
              previousExp,
              previousExpTime: previousExp
                ? new Date(previousExp * 1000).toLocaleString()
                : "(none)",
              latestExp: decodedLatest.exp,
              latestExpTime: new Date(decodedLatest.exp * 1000).toLocaleString(),
              latestBufferedSecondsLeft,
              latestRealSecondsLeft,
            }
          );
          await signOut();
          return;
        }
      }

      if (previousExp && decodedLatest.exp <= previousExp) {
        acceptedSameTokenExpRef.current = decodedLatest.exp;
        acceptedSameTokenSuppressUntilRef.current =
          Date.now() + SAME_TOKEN_WARNING_SUPPRESS_MS;

        console.warn(
          "[AuthContext] Renew returned same token exp, but real token is still valid. Temporarily suppressing repeat warning for same token.",
          {
            previousExp,
            previousExpTime: new Date(previousExp * 1000).toLocaleString(),
            latestExp: decodedLatest.exp,
            latestExpTime: new Date(decodedLatest.exp * 1000).toLocaleString(),
            latestBufferedSecondsLeft,
            latestRealSecondsLeft,
            suppressUntilTime: new Date(
              acceptedSameTokenSuppressUntilRef.current
            ).toLocaleString(),
          }
        );
      } else {
        clearSameTokenWarningSuppression();
      }

      const now = Date.now();
      lastInteractionAtRef.current = now;
      writeSharedLastActivityAt(now);

      showWarningRef.current = false;
      clearWarningState();
      cleanupStaleDialogArtifacts();

      console.log("[AuthContext] Session refreshed successfully. Staying on same page.", {
        newExp: decodedLatest.exp,
        newExpTime: new Date(decodedLatest.exp * 1000).toLocaleString(),
        bufferedExpTime: new Date(
          (getBufferedExp(decodedLatest.exp) || 0) * 1000
        ).toLocaleString(),
        latestBufferedSecondsLeft,
        latestRealSecondsLeft,
        markedActivityAt: new Date(now).toLocaleString(),
      });
    } catch (err) {
      console.error("[AuthContext] Failed to extend session", err);
      await signOut();
      return;
    } finally {
      setIsExtendingSession(false);
      cleanupStaleDialogArtifacts();
    }

    rescheduleWarnings("extend-session-success");
  };

  const handleLogoutNow = async () => {
    console.warn("[ODP-CROSS-TAB-DEBUG] NO_BUTTON_CLICKED", { origin: window.location.origin, path: window.location.pathname, warningReason: warningReasonRef.current, logoutInProgress: isCrossTabLogoutInProgress() });
    setShowInactivityWarning(false);
    setUserChoseLogout(true);
    await signOut();
  };

  const formatTime = (seconds: number): string => {
    const safeSeconds = Math.max(0, seconds);
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const getWarningMessage = () => {
    if (userChoseLogout) {
      return "Logging out...";
    }

    if (warningReason === "token-expiring") {
      return "Your access token is about to expire.";
    }

    return "Your session will expire due to inactivity.";
  };

  return (
    <AuthContext.Provider
      value={{
        authService,
        authProvider: normalizedAuthProvider,
        authType: normalizedAuthProvider,
        isAuthenticated,
        setIsAuthenticated,
        appUser: appUserWithReviewerType,
        setUser,
        isUserValidated,
        setIsUserValidated,
        signIn,
        signOut,
        isAdministrator,
        isSuperAdministrator,
        hasActiveSession,
        isFinalReviewer,
        isSmeReviewer,
        isInitialReviewer,
        showInactivityWarning,
        extendSession: () => {
          void handleExtendSession();
        },
        currentUserRoles,
        isAuthorizedUser,
      }}
    >
      {children}

      {showInactivityWarning && (
        <Dialog
          visible={showInactivityWarning}
          modal
          closable={false}
          header="Session Timeout Warning"
          style={{ width: "400px" }}
          className="inactivity-warning-dialog"
          onHide={() => {}}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{ textAlign: "center", padding: "20px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <i
              className="pi pi-exclamation-triangle"
              style={{ fontSize: "3rem", color: "#f39c12", marginBottom: "1rem" }}
            />
            <p style={{ marginBottom: "1rem", fontSize: "16px" }}>
              {getWarningMessage()}
            </p>

            <div
              style={{
                backgroundColor: "#fff3cd",
                border: "1px solid #ffeaa7",
                borderRadius: "4px",
                padding: "10px",
                marginBottom: "1.5rem",
                fontSize: "18px",
                fontWeight: "bold",
                color: "#856404",
              }}
            >
              Time remaining: {formatTime(countdownSeconds)}
            </div>

            {!userChoseLogout ? (
              <>
                <p
                  style={{ marginBottom: "1.5rem", fontSize: "14px", color: "#666" }}
                >
                  Would you like to stay logged in?
                </p>
                <div
                  style={{ display: "flex", gap: "1rem", justifyContent: "center" }}
                >
                  <Button
                    label={isExtendingSession ? "Refreshing..." : "Yes"}
                    icon="pi pi-check"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleExtendSession();
                    }}
                    className="p-button-success"
                    disabled={isExtendingSession}
                  />
                  <Button
                    label="No"
                    icon="pi pi-sign-out"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleLogoutNow();
                    }}
                    className="p-button-secondary"
                    disabled={isExtendingSession}
                  />
                </div>
              </>
            ) : (
              <p style={{ fontSize: "14px", color: "#666", fontStyle: "italic" }}>
                You will be logged out when the timer reaches zero.
              </p>
            )}
          </div>
        </Dialog>
      )}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};